import CustomButton from "../../../../components/common/CustomButton";
import React, { useEffect, useState } from "react";
import { Input, Form, Typography, message } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux"; // Import useDispatch
import { verifyOtp } from "../../../../features/LoginThunk/VerifyOtpThunk";
// import { fetchUserPermissions } from "../../../../features/Permissions/UserPermissions";
import { useSelector } from "react-redux";
import { clearUserData } from "../../../../features/user/UserSlices";
import { clearPermissions } from "../../../../features/Permissions/UserPermissionsSlice";
import { resolvePostOtpRouteWithAccess } from "../authFlow";

const clearStoredAuthSession = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("userDetails");
};

const DEFAULT_RESEND_COOLDOWN = 60;
const MAX_RESEND_ATTEMPTS = 3;

const getRemainingCooldown = () => {
  const lastRequestedAt = Number(localStorage.getItem("lastOtpRequestedAt"));
  if (!lastRequestedAt) return DEFAULT_RESEND_COOLDOWN;
  const elapsedSeconds = Math.floor((Date.now() - lastRequestedAt) / 1000);
  return Math.max(DEFAULT_RESEND_COOLDOWN - elapsedSeconds, 0);
};

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 45);
  const remainingSeconds = seconds % 45;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const maskMobileNumber = (mobileNumber = "") => {
  if (mobileNumber.length < 4) return mobileNumber;
  return `${mobileNumber.slice(0, 2)}******${mobileNumber.slice(-2)}`;
};

const OtpForm = () => {
  const navigate = useNavigate();
  const { Title } = Typography;
  const [otp, setOtp] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { id } = useParams();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userDetails);
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(getRemainingCooldown);
  const [isResending, setIsResending] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const mobileNumber = id || localStorage.getItem("mobileNumber") || "";

  const handleChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtp(value);
    }
  };

  useEffect(() => {
    setOtp("");
    clearStoredAuthSession();
    dispatch(clearUserData());
    dispatch(clearPermissions());
  }, [id, dispatch]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timerId = setInterval(() => {
      setResendCountdown((currentCountdown) => Math.max(currentCountdown - 1, 0));
    }, 1000);
    return () => clearInterval(timerId);
  }, [resendCountdown]);

  const toggleVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      message.error("Please enter a valid 6-digit OTP.");
      return;
    }

    const otpValue = otp; // Combine OTP array into a string
    if (otpValue.length !== 6) {
      message.error("Please enter a valid 6-digit OTP.");
      return;
    }

    // Dispatch the verifyOtp thunk action with the necessary parameters
    try {
      setLoading(true);
      const result = await dispatch(verifyOtp({ mobileNumber, otpValue }));

      if (result && result.error) {
        message.error("Please enter valid OTP.");
      } else {
        const latestUserData = result?.payload?.userDetails || userData;
        const targetRoute = await resolvePostOtpRouteWithAccess(
          latestUserData,
          "",
          result?.payload?.permissions,
        );

        message.success("Otp Verified!");
        navigate(targetRoute);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      message.error("Failed to verify OTP. Please try again.");
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (isResending || resendCountdown > 0) return;
    if (resendAttempts >= MAX_RESEND_ATTEMPTS) {
      message.warning("Too many resend attempts. Please complete additional verification or contact support.");
      return;
    }
    if (!mobileNumber) {
      message.error("Mobile number is missing. Please request OTP again.");
      return;
    }
    try {
      setIsResending(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/user/requestOTP`, { mobileNumber, resend: true });
      const cooldownSeconds = Number(response?.data?.cooldownSeconds) || DEFAULT_RESEND_COOLDOWN;
      localStorage.setItem("mobileNumber", mobileNumber);
      localStorage.setItem("lastOtpRequestedAt", Date.now().toString());
      setResendAttempts((attempts) => attempts + 1);
      setResendCountdown(cooldownSeconds);
      message.success("OTP resent successfully.");
    } catch (error) {
      const retryAfter = Number(error?.response?.data?.retryAfter) || Number(error?.response?.headers?.["retry-after"]) || 0;
      const serverMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to resend OTP. Please try again.";
      if (retryAfter > 0) {
        localStorage.setItem("lastOtpRequestedAt", Date.now().toString());
        setResendCountdown(retryAfter);
        message.error(`${serverMessage} Try again in ${retryAfter} seconds.`);
      } else {
        message.error(serverMessage);
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="otp-form-container">
      {/* <Title level={2}>Enter OTP</Title> */}
      <form onSubmit={handleSubmit}>
        {mobileNumber && (
          <div style={{ marginBottom: "10px", textAlign: "center" }}>
            OTP sent to {maskMobileNumber(mobileNumber)}
          </div>
        )}
        <div className="gap-2 my-3">
          <Input
            type={isPasswordVisible ? "text" : "password"}
            value={otp}
            onChange={handleChange}
            maxLength={6}
            placeholder="Enter OTP"
            className="input-default"
            suffix={
              <span onClick={toggleVisibility} style={{ cursor: "pointer" }}>
                {isPasswordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </span>
            }
          />
          {/* <div
            style={{
              textAlign: "center",
              marginBottom: "10px",
              marginTop: "10px",
            }}
          >
            <span
              onClick={toggleVisibility}
              style={{ cursor: "pointer", fontSize: "24px" }}
            >
              {isPasswordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </span>
          </div> */}
        </div>

        <Form.Item>
          <CustomButton
            type="primary"
            htmlType="submit"
            onClick={handleSubmit}
            loading={loading}
            className="login-button"
            block
          >
            {loading ? "Submitting OTP..." : "Submit OTP"}
          </CustomButton>
        </Form.Item>
        <Form.Item>
          <CustomButton type="default" onClick={handleResendOtp} loading={isResending} disabled={resendCountdown > 0 || isResending} className="login-button" block>
            {isResending ? "Resending OTP..." : resendCountdown > 0 ? `Resend OTP in ${formatCountdown(resendCountdown)}` : "Resend OTP"}
          </CustomButton>
        </Form.Item>
      </form>
    </div>
  );
};

export default OtpForm;
